import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import axios from 'axios';

const WaterQualityParameters = () => {
  const [parameters, setParameters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingParam, setEditingParam] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    safeMinValue: '',
    safeMaxValue: '',
    warningMinValue: '',
    warningMaxValue: '',
    alarmMinValue: '',
    alarmMaxValue: '',
    description: '',
    isActive: true
  });

  // Fetch parameters from the API
  const fetchParameters = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/water-quality/parameters`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setParameters(response.data.data);
    } catch (error) {
      console.error('Error fetching parameters:', error);
      toast.error('Failed to fetch water quality parameters');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchParameters();
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingParam) {
        // Update existing parameter
        await axios.put(
          `${process.env.REACT_APP_API_URL}/water-quality/parameters/${editingParam.id}`,
          formData,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          }
        );
        toast.success('Parameter updated successfully');
      } else {
        // Create new parameter
        await axios.post(
          `${process.env.REACT_APP_API_URL}/water-quality/parameters`,
          formData,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          }
        );
        toast.success('Parameter created successfully');
      }
      
      // Reset form and refresh the list
      setFormData({
        name: '',
        displayName: '',
        safeMinValue: '',
        safeMaxValue: '',
        warningMinValue: '',
        warningMaxValue: '',
        alarmMinValue: '',
        alarmMaxValue: '',
        description: '',
        isActive: true
      });
      setEditingParam(null);
      fetchParameters();
    } catch (error) {
      console.error('Error saving parameter:', error);
      toast.error(error.response?.data?.message || 'Failed to save parameter');
    }
  };

  // Handle edit button click
  const handleEdit = (param) => {
    setEditingParam(param);
    setFormData({
      name: param.name,
      displayName: param.displayName,
      safeMinValue: param.safeMinValue ?? '',
      safeMaxValue: param.safeMaxValue ?? '',
      warningMinValue: param.warningMinValue ?? '',
      warningMaxValue: param.warningMaxValue ?? '',
      alarmMinValue: param.alarmMinValue ?? '',
      alarmMaxValue: param.alarmMaxValue ?? '',
      description: param.description || '',
      isActive: param.isActive
    });
  };

  // Handle delete button click
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this parameter?')) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_URL}/water-quality/parameters/${id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        toast.success('Parameter deleted successfully');
        fetchParameters();
      } catch (error) {
        console.error('Error deleting parameter:', error);
        toast.error('Failed to delete parameter');
      }
    }
  };

  // Toggle parameter status
  const toggleStatus = async (param, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/water-quality/parameters/${param.id}`,
        { isActive: !param.isActive },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data.success) {
        toast.success(`Parameter ${!param.isActive ? 'activated' : 'deactivated'} successfully`);
        fetchParameters();
      } else {
        throw new Error(response.data.message || 'Failed to toggle parameter status');
      }
    } catch (error) {
      console.error('Error toggling parameter status:', error);
      toast.error(error.response?.data?.message || `Failed to ${param.isActive ? 'deactivate' : 'activate'} parameter`);
    }
    
    return false;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 mt-16">
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Main Content - Parameter List (2/3 width) */}
        <div className="lg:w-2/3">
          <div className="bg-white shadow-lg rounded-xl overflow-hidden border border-gray-200">
            <div className="px-6 py-5 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Water Quality Parameters
                  </h2>
                  <p className="mt-1 text-sm text-gray-500">
                    {parameters.length} parameters configured
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {parameters.filter(p => p.isActive).length} Active
                  </span>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    {parameters.filter(p => !p.isActive).length} Inactive
                  </span>
                </div>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Parameter
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ranges
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {parameters.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="px-6 py-8 text-center">
                        <div className="text-gray-500">
                          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p className="mt-2 text-sm font-medium">
                            No parameters found
                          </p>
                          <p className="mt-1 text-sm">
                            Add your first parameter using the form
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    parameters.map((param) => (
                      <tr key={param.id} className={!param.isActive ? 'bg-gray-50 opacity-80' : 'hover:bg-gray-50'}>
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {param.displayName}
                              </div>
                              <div className="text-sm text-gray-500">
                                {param.name}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            <div className="text-sm">
                              <span className="font-medium text-gray-500">Safe:</span>{' '}
                              <span className="text-gray-700">
                                {param.safeMinValue ?? 'N/A'} - {param.safeMaxValue ?? 'N/A'}
                              </span>
                            </div>
                            <div className="text-sm">
                              <span className="font-medium text-yellow-500">Warning:</span>{' '}
                              <span className="text-gray-700">
                                {param.warningMinValue ?? 'N/A'} - {param.warningMaxValue ?? 'N/A'}
                              </span>
                            </div>
                            <div className="text-sm">
                              <span className="font-medium text-red-500">Alarm:</span>{' '}
                              <span className="text-gray-700">
                                {param.alarmMinValue ?? 'N/A'} - {param.alarmMaxValue ?? 'N/A'}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span 
                            className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              param.isActive 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {param.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-3">
                            <button
                              onClick={() => handleEdit(param)}
                              className="text-indigo-600 hover:text-indigo-900"
                              title="Edit"
                            >
                              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              type="button"
                              onClick={(e) => toggleStatus(param, e)}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                                param.isActive ? 'bg-indigo-600' : 'bg-gray-200'
                              }`}
                              title={param.isActive ? 'Deactivate' : 'Activate'}
                            >
                              <span className="sr-only">Toggle status</span>
                              <span
                                className={`${
                                  param.isActive ? 'translate-x-6' : 'translate-x-1'
                                } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                              />
                            </button>
                            <button
                              onClick={() => handleDelete(param.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete"
                            >
                              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Sidebar - Parameter Form (1/3 width) */}
        <div className="lg:w-1/3">
          <div className="bg-white rounded-xl shadow-md p-6 border border-gray-200 sticky top-4">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              {editingParam ? 'Edit Parameter' : 'Add New Parameter'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name (unique identifier)
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  required
                  disabled={!!editingParam}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Name
                </label>
                <input
                  type="text"
                  name="displayName"
                  value={formData.displayName}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <h4 className="text-xs font-medium text-gray-700 mb-2">Safe Min</h4>
                  <input
                    type="number"
                    step="0.01"
                    name="safeMinValue"
                    value={formData.safeMinValue}
                    onChange={handleInputChange}
                    className="w-full px-3 py-1 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Min"
                  />
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <h4 className="text-xs font-medium text-gray-700 mb-2">Safe Max</h4>
                  <input
                    type="number"
                    step="0.01"
                    name="safeMaxValue"
                    value={formData.safeMaxValue}
                    onChange={handleInputChange}
                    className="w-full px-3 py-1 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Max"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <h4 className="text-xs font-medium text-gray-700 mb-2">Warning Min</h4>
                  <input
                    type="number"
                    step="0.01"
                    name="warningMinValue"
                    value={formData.warningMinValue}
                    onChange={handleInputChange}
                    className="w-full px-3 py-1 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Min"
                  />
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <h4 className="text-xs font-medium text-gray-700 mb-2">Warning Max</h4>
                  <input
                    type="number"
                    step="0.01"
                    name="warningMaxValue"
                    value={formData.warningMaxValue}
                    onChange={handleInputChange}
                    className="w-full px-3 py-1 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Max"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <h4 className="text-xs font-medium text-gray-700 mb-2">Alarm Min</h4>
                  <input
                    type="number"
                    step="0.01"
                    name="alarmMinValue"
                    value={formData.alarmMinValue}
                    onChange={handleInputChange}
                    className="w-full px-3 py-1 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Min"
                  />
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <h4 className="text-xs font-medium text-gray-700 mb-2">Alarm Max</h4>
                  <input
                    type="number"
                    step="0.01"
                    name="alarmMaxValue"
                    value={formData.alarmMaxValue}
                    onChange={handleInputChange}
                    className="w-full px-3 py-1 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Max"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="2"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700">
                  Active Parameter
                </label>
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                {editingParam && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingParam(null);
                      setFormData({
                        name: '',
                        displayName: '',
                        safeMinValue: '',
                        safeMaxValue: '',
                        warningMinValue: '',
                        warningMaxValue: '',
                        alarmMinValue: '',
                        alarmMaxValue: '',
                        description: '',
                        isActive: true
                      });
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="submit"
                  className="px-6 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  {editingParam ? 'Update' : 'Add Parameter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WaterQualityParameters; 